"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

interface KaalSarpType {
  number: number;
  name: string;
  hindi: string;
  axis: string;
  rahuHouse: number;
  ketuHouse: number;
  coreThemes: string;
  symptoms: string[];
  remedy: string;
}

const KAAL_SARP_TYPES: KaalSarpType[] = [
  {
    number: 1,
    name: "Anant Kaal Sarp",
    hindi: "अनंत कालसर्प",
    axis: "1st House (Lagna) — 7th House (Kalatra)",
    rahuHouse: 1,
    ketuHouse: 7,
    coreThemes: "Personality, physical health, and marriage harmony",
    symptoms: [
      "Mental restlessness and frequent self-doubt",
      "Delayed marriage or marital friction",
      "Struggle in partnership ventures",
    ],
    remedy:
      "Chant Maha Mrityunjaya Mantra (108 daily), offer silver snake pair in flowing river on Nag Panchami.",
  },
  {
    number: 2,
    name: "Kulik Kaal Sarp",
    hindi: "कुलिक कालसर्प",
    axis: "2nd House (Dhana) — 8th House (Ayur)",
    rahuHouse: 2,
    ketuHouse: 8,
    coreThemes: "Family wealth, speech, and sudden expenses",
    symptoms: [
      "Financial instability despite good earnings",
      "Misunderstandings in speech with family",
      "Chronic eye or throat issues",
    ],
    remedy:
      "Donate copper vessels and wheat on Tuesdays, refrain from smoking/alcohol, recite Hanuman Chalisa.",
  },
  {
    number: 3,
    name: "Vasuki Kaal Sarp",
    hindi: "वासुकी कालसर्प",
    axis: "3rd House (Sahaja) — 9th House (Bhagya)",
    rahuHouse: 3,
    ketuHouse: 9,
    coreThemes: "Courage, sibling relations, and fortune",
    symptoms: [
      "Disputes or coldness with younger siblings",
      "Efforts feel under-rewarded by luck",
      "Occasional sudden loss of confidence",
    ],
    remedy:
      "Feed birds daily with 7 mixed grains (Satnaja), observe Pradosh Vrats dedicated to Lord Shiva.",
  },
  {
    number: 4,
    name: "Shankhpal Kaal Sarp",
    hindi: "शंखपाल कालसर्प",
    axis: "4th House (Sukha) — 10th House (Karma)",
    rahuHouse: 4,
    ketuHouse: 10,
    coreThemes: "Domestic peace, mother's health, and land/vehicles",
    symptoms: [
      "Frequent obstacles in purchasing property or vehicles",
      "Anxiety and lack of inner emotional peace",
      "Mother experiences recurring health issues",
    ],
    remedy:
      "Perform milk abhishekam on Shivling on Mondays, wear a certified natural silver chain.",
  },
  {
    number: 5,
    name: "Padma Kaal Sarp",
    hindi: "पद्म कालसर्प",
    axis: "5th House (Putra) — 11th House (Labha)",
    rahuHouse: 5,
    ketuHouse: 11,
    coreThemes: "Children, higher studies, and creative intellect",
    symptoms: [
      "Delays in childbirth or worries concerning children",
      "Distractions during academic exams",
      "Losses in stock speculation",
    ],
    remedy:
      "Recite Saraswati Vandana and Gayatri Mantra daily, feed stray cows green fodder on Wednesdays.",
  },
  {
    number: 6,
    name: "Mahapadma Kaal Sarp",
    hindi: "महापद्म कालसर्प",
    axis: "6th House (Shatru) — 12th House (Vyaya)",
    rahuHouse: 6,
    ketuHouse: 12,
    coreThemes: "Overcoming enemies, health, and foreign travel",
    symptoms: [
      "Secret enemies creating silent hurdles",
      "Recurring health dips without clear medical cause",
      "High legal or medical expenditures",
    ],
    remedy:
      "Perform Rudrabhishekam at Mahakaleshwar or Trimbakeshwar temple, chant Rahu Beej Mantra.",
  },
  {
    number: 7,
    name: "Takshak Kaal Sarp",
    hindi: "तक्षक कालसर्प",
    axis: "7th House (Kalatra) — 1st House (Lagna)",
    rahuHouse: 7,
    ketuHouse: 1,
    coreThemes: "Marital partnership and public business relations",
    symptoms: [
      "Intense friction in business partnerships",
      "Delays in finding a compatible life partner",
      "Sudden detachment in relationships",
    ],
    remedy:
      "Perform Rahu-Ketu Shanti puja with partner, offer coconut in flowing water on Saturdays.",
  },
  {
    number: 8,
    name: "Karkotak Kaal Sarp",
    hindi: "कर्कोटक कालसर्प",
    axis: "8th House (Ayur) — 2nd House (Dhana)",
    rahuHouse: 8,
    ketuHouse: 2,
    coreThemes: "Longevity, sudden inheritance, and occult knowledge",
    symptoms: [
      "Sudden ups and downs in fortunes",
      "Difficulty receiving paternal inheritance",
      "Frequent unexplainable fear of the unknown",
    ],
    remedy: "Worship Lord Bhairava or Mahakali on Ashtami tithi, recite Shiva Tandava Stotram.",
  },
  {
    number: 9,
    name: "Shankhachur Kaal Sarp",
    hindi: "शंखचूड़ कालसर्प",
    axis: "9th House (Bhagya) — 3rd House (Sahaja)",
    rahuHouse: 9,
    ketuHouse: 3,
    coreThemes: "Dharma, guru blessings, and paternal relations",
    symptoms: [
      "Strained relationship or early separation from father",
      "Skepticism or crises in spiritual faith",
      "Slow rise in luck during youth",
    ],
    remedy:
      "Respect elders and teachers, donate yellow clothes and brass vessels to temple priests on Thursdays.",
  },
  {
    number: 10,
    name: "Ghatak Kaal Sarp",
    hindi: "घातक कालसर्प",
    axis: "10th House (Karma) — 4th House (Sukha)",
    rahuHouse: 10,
    ketuHouse: 4,
    coreThemes: "Professional career, authority, and public status",
    symptoms: [
      "Frequent job changes or office politics hurdles",
      "Unfair denial of earned promotions",
      "Struggle to maintain public reputation",
    ],
    remedy:
      "Recite Aditya Hridaya Stotram every morning, offer Arghya with water and red kumkum to rising Sun.",
  },
  {
    number: 11,
    name: "Vishdhar Kaal Sarp",
    hindi: "विषधर कालसर्प",
    axis: "11th House (Labha) — 5th House (Putra)",
    rahuHouse: 11,
    ketuHouse: 5,
    coreThemes: "Cash flow gains, elder siblings, and social network",
    symptoms: [
      "Fluctuating monthly income streams",
      "Disappointments from close friends or elder siblings",
      "Restlessness in sleep and frequent bad dreams",
    ],
    remedy:
      "Perform Vishnu Sahasranama Japa on Ekadashi days, donate blankets to needy people in winter.",
  },
  {
    number: 12,
    name: "Sheshnag Kaal Sarp",
    hindi: "शेषनाग कालसर्प",
    axis: "12th House (Vyaya) — 6th House (Shatru)",
    rahuHouse: 12,
    ketuHouse: 6,
    coreThemes: "Expenses, foreign settlements, and spiritual liberation",
    symptoms: [
      "Excessive expenditure on litigation or hospital bills",
      "Mental insomnia and restless subconscious",
      "Feeling misunderstood by society",
    ],
    remedy:
      "Chant 'Om Namah Shivaya' (1008 times on Monday nights), light mustard oil lamp for Saturn on Saturdays.",
  },
];

export default function KaalSarpTypeMatrixStudio() {
  const [selectedNum, setSelectedNum] = useState<number>(1);

  const type = useMemo<KaalSarpType>(() => {
    return KAAL_SARP_TYPES.find((t) => t.number === selectedNum) || KAAL_SARP_TYPES[0];
  }, [selectedNum]);

  const handleConsult = () => {
    const prompt = `Please check if my Janam Kundali has ${type.name} (${type.hindi}) on the ${type.axis} axis. Is it Purna or Anshik, and what are the exact Vedic remedies?`;
    try {
      safeSessionSet("seo_entry_intent", "dosha_remedy");
      safeSessionSet("seo_entry_path", "/kaal-sarp-dosha");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader />
      <TypeSelector selectedNum={selectedNum} onSelect={setSelectedNum} />
      <TypeDetailCard type={type} onConsult={handleConsult} />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Vedic Dosha Diagnostic
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        12 Types of Kaal Sarp Dosha Calculator &amp; Effects (काल सर्प दोष)
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Check any of the 12 classical Rahu-Ketu formations by house axis to evaluate life effects
        and prescribed shastric remedies.
      </p>
    </div>
  );
}

function TypeSelector({
  selectedNum,
  onSelect,
}: {
  selectedNum: number;
  onSelect: (n: number) => void;
}) {
  return (
    <div className="mb-6">
      <label
        htmlFor="kaal-sarp-select"
        className="block text-caption font-bold uppercase tracking-wider text-text-muted mb-2"
      >
        Select Kaal Sarp Type (1–12)
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {KAAL_SARP_TYPES.map((t) => {
          const isSelected = t.number === selectedNum;
          return (
            <button
              key={t.number}
              type="button"
              onClick={() => onSelect(t.number)}
              className={`p-2.5 rounded-xl border text-left transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
              }`}
            >
              <span className="block text-caption font-bold text-intent-brand">
                Type #{t.number}
              </span>
              <span className="block text-body-small font-bold text-text-primary truncate">
                {t.name.replace(" Kaal Sarp", "")}
              </span>
              <span className="block text-caption text-text-muted">{t.hindi}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TypeDetailCard({ type, onConsult }: { type: KaalSarpType; onConsult: () => void }) {
  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/kaal-sarp-dosha"),
    "/kaal-sarp-dosha",
    "en"
  );

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              #{type.number} {type.name} ({type.hindi})
            </h3>
            <span className="px-3 py-0.5 rounded-full text-caption font-bold bg-intent-brand/10 text-intent-brand">
              Rahu {type.rahuHouse}H &middot; Ketu {type.ketuHouse}H
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            House Axis: <strong className="text-text-primary">{type.axis}</strong>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Check My Chart for Type #{type.number} ✦
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-2">
            Primary Symptom Manifestations
          </p>
          <ul className="space-y-1.5 text-caption text-text-primary">
            {type.symptoms.map((s, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-warning mt-0.5">⚠️</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 rounded-xl bg-intent-brand/5 border border-intent-brand/20">
          <p className="text-caption font-bold uppercase tracking-wider text-intent-brand mb-2">
            Prescribed Shastric Upay (Remedies)
          </p>
          <p className="text-caption text-text-primary leading-relaxed">{type.remedy}</p>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          Not sure if your planetary degrees create a Purna or Anshik Kaal Sarp?
        </span>
        <Link
          href="/free-kundali/"
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Calculate Janam Kundali Degrees →
        </Link>
      </div>
    </div>
  );
}
